import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { trackEvent } from "../lib/analytics";
import { Film, Compass, Radar as RadarIcon } from "lucide-react";

/**
 * ActivationChoice — one-shot modal shown right after GuidedTour finishes
 * for a brand-new user. There is NO dismiss / X / "later" — the user must
 * commit to one of the two paths. Backend `first_activation_choice` is the
 * source of truth; localStorage is a belt-and-suspenders guard against
 * repeat display in the same session.
 */

const COPY = {
  pl: {
    title: "Od czego zaczynamy?",
    sceneTitle: "PRACUJĘ NAD SCENĄ",
    sceneBody: "Mam scenę, casting albo self-tape. Przeanalizuj tekst albo od razu przejdź do próby z AI Readerem.",
    careerTitle: "CHCĘ RUSZYĆ Z KARIERĄ",
    careerBody: "Wybierz cel i przejdź przez konkretną ścieżkę działania krok po kroku.",
    radarTitle: "MÓJ PROFIL AKTORSKI",
    radarBody: "Określ, kim jesteś jako aktor, w jakich rolach widzi Cię świat, jakie role Ty chcesz grać i jakie wartości chcesz wnosić do pracy.",
    recommendedBadge: "REKOMENDOWANE",
  },
  en: {
    title: "What do you want to work on first?",
    sceneTitle: "WORK ON A SCENE",
    sceneBody: "I have a scene, audition or self-tape. Analyse the script or go straight to rehearsal with the AI Reader.",
    careerTitle: "MOVE MY CAREER FORWARD",
    careerBody: "Pick a goal and walk through a concrete step-by-step Career Path.",
    radarTitle: "MY ACTOR PROFILE",
    radarBody: "Define who you are as an actor, the roles others see you in, the roles you want to play, and the values you want to bring to your work.",
    recommendedBadge: "RECOMMENDED",
  },
};

const LS_KEY = (uid) => `leo_activation_choice_seen_v1_${uid || "anon"}`;

export default function ActivationChoice() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(null); // "scene" | "career" | null
  const nav = useNavigate();
  const lang = (user?.lang || "pl") === "en" ? "en" : "pl";
  const t = COPY[lang];

  const shouldRender = () => {
    if (!user) return false;
    if (user.needs_first_onboarding === true) return false;
    if (user.first_activation_choice) return false;
    try { if (window.localStorage.getItem(LS_KEY(user.user_id)) === "1") return false; } catch {}
    return true;
  };

  useEffect(() => {
    const handler = () => {
      if (shouldRender()) {
        setOpen(true);
        trackEvent("activation_choice_shown");
      }
    };
    window.addEventListener("leo:tour_finished", handler);
    return () => window.removeEventListener("leo:tour_finished", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, user?.first_activation_choice, user?.needs_first_onboarding]);

  const commit = async (choice) => {
    setSubmitting(choice);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const token = window.localStorage.getItem("leo_token");
      await fetch(`${base}/api/user/activation-choice`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ choice }),
      });
      try { window.localStorage.setItem(LS_KEY(user.user_id), "1"); } catch {}
      trackEvent(
        choice === "scene"
          ? "activation_choice_scene"
          : choice === "radar"
            ? "activation_choice_radar"
            : choice === "paths"
              ? "activation_choice_paths"
              : "activation_choice_career"
      );
      // Update in-memory user so the modal doesn't try to re-render before
      // navigation. Backend already persisted the choice.
      if (setUser) {
        setUser((u) => (u ? { ...u, first_activation_choice: choice, first_activation_choice_at: new Date().toISOString() } : u));
      }
      setOpen(false);
      if (choice === "scene") {
        nav("/scenes/new");
      } else if (choice === "radar") {
        nav("/radar");
      } else if (choice === "paths") {
        nav("/career?view=actor_profile");
      } else {
        nav("/career");
      }
    } finally {
      setSubmitting(null);
    }
  };

  if (!open || !shouldRender()) return null;

  return (
    <div
      data-testid="activation-choice-modal"
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-stone-950/85 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-4xl">
        <h1 className="text-center text-3xl sm:text-4xl lg:text-5xl font-light text-stone-100 mb-8 tracking-tight">
          {t.title}
        </h1>
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          <button
            data-testid="activation-scene-btn"
            disabled={!!submitting}
            onClick={() => commit("scene")}
            className="group text-left rounded-2xl border border-stone-700 hover:border-amber-500 bg-stone-900/60 hover:bg-stone-900 p-8 transition-all disabled:opacity-60"
          >
            <Film className="w-8 h-8 text-amber-500 mb-4" />
            <div className="mono text-xs uppercase tracking-widest text-amber-500 mb-3">01</div>
            <h2 className="text-xl sm:text-2xl font-medium text-stone-100 mb-3">{t.sceneTitle}</h2>
            <p className="text-sm text-stone-400 leading-relaxed">{t.sceneBody}</p>
          </button>
          <button
            data-testid="activation-career-btn"
            disabled={!!submitting}
            onClick={() => commit("career")}
            className="group text-left rounded-2xl border border-stone-700 hover:border-amber-500 bg-stone-900/60 hover:bg-stone-900 p-8 transition-all disabled:opacity-60"
          >
            <Compass className="w-8 h-8 text-amber-500 mb-4" />
            <div className="mono text-xs uppercase tracking-widest text-amber-500 mb-3">02</div>
            <h2 className="text-xl sm:text-2xl font-medium text-stone-100 mb-3">{t.careerTitle}</h2>
            <p className="text-sm text-stone-400 leading-relaxed">{t.careerBody}</p>
          </button>
          <button
            data-testid="activation-radar-btn"
            disabled={!!submitting}
            onClick={() => commit("paths")}
            className="group text-left rounded-2xl border border-stone-700 hover:border-amber-500 bg-stone-900/60 hover:bg-stone-900 p-8 transition-all disabled:opacity-60 relative"
          >
            <span className="absolute top-3 right-3 mono text-[10px] uppercase tracking-widest text-amber-500 border border-amber-500/40 rounded px-2 py-0.5">
              {t.recommendedBadge}
            </span>
            <RadarIcon className="w-8 h-8 text-amber-500 mb-4" />
            <div className="mono text-xs uppercase tracking-widest text-amber-500 mb-3">03</div>
            <h2 className="text-xl sm:text-2xl font-medium text-stone-100 mb-3">{t.radarTitle}</h2>
            <p className="text-sm text-stone-400 leading-relaxed">{t.radarBody}</p>
          </button>
        </div>
      </div>
    </div>
  );
}
